import { useQueryClient } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { PlusInSquareIcon } from "assets/icons";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useDebouncedValue } from "hooks";
import {
  ChangeDriverDeleteStatusModal,
  Driver,
  DriversTable,
  useGetDrivers,
  useUpdateDriver
} from "modules/drivers";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Pagination, SearchInput } from "ui";

import { CompanyAddDriverModal } from "../../../components";

import styles from "./CompanyTabShared.module.scss";

interface CompanyDriversTabProps {
  companyId: string;
}

const CompanyDriversTab = ({ companyId }: CompanyDriversTabProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedValue<string>(searchValue, 500);
  const [sorting, setSorting] = useState<SortingState>([]);

  const requestParams = useMemo(
    () => ({
      page,
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch,
      orderBy: sorting[0]?.id ?? null,
      order: sorting[0] ? (sorting[0]?.desc ? "DESC" : "ASC") : "ASC",
      isDeleted: false,
      deliveryCompanyId: companyId
    }),
    [page, debouncedSearch, sorting, companyId]
  );

  const { drivers, meta, isLoading } = useGetDrivers(
    requestParams as unknown as Parameters<typeof useGetDrivers>[0]
  );

  const [localDrivers, setLocalDrivers] = useState<Driver[]>([]);

  useEffect(() => {
    setLocalDrivers(drivers ?? []);
  }, [drivers]);

  const totalItems = meta?.itemCount ?? 0;

  const [driverToChangeDeleteStatus, setDriverToChangeDeleteStatus] =
    useState<Driver | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editableDriverId, setEditableDriverId] = useState<string | null>(null);

  const invalidateDrivers = () => {
    void queryClient.invalidateQueries({
      queryKey: ["driver", "admin", "all"]
    });
  };

  const updateDriverMutation = useUpdateDriver({
    onSuccess: () => invalidateDrivers()
  });

  const openCreate = () => {
    setEditableDriverId(null);
    setIsFormOpen(true);
  };

  const openEdit = (driver: Driver) => {
    setEditableDriverId(driver.id);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditableDriverId(null);
  };

  return (
    <div className={styles.tab}>
      <div className={styles.tab__toolbar}>
        <div className={styles.tab__toolbarLeft}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={t("Search...")}
          />
        </div>
        <div className={styles.tab__toolbarActions}>
          <Button
            icon={<PlusInSquareIcon width={20} height={20} />}
            onClick={openCreate}
          >
            {t("New Driver")}
          </Button>
        </div>
      </div>

      <DriversTable
        driversList={localDrivers}
        setDrivers={setLocalDrivers}
        isLoading={isLoading}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={openEdit}
        onChangeDeleteStatus={setDriverToChangeDeleteStatus}
        enableDragDrop
        isDragDisabled={sorting.length > 0 || !!debouncedSearch}
      />

      <Pagination
        currentPage={page}
        onChange={setPage}
        perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
        totalItems={totalItems}
      />

      <CompanyAddDriverModal
        isOpen={isFormOpen}
        companyId={companyId}
        editableDriverId={editableDriverId}
        onClose={closeForm}
        onCreated={invalidateDrivers}
      />

      <ChangeDriverDeleteStatusModal
        id={String(driverToChangeDeleteStatus?.id)}
        isOpen={Boolean(driverToChangeDeleteStatus)}
        onClose={() => setDriverToChangeDeleteStatus(null)}
        action={driverToChangeDeleteStatus?.isDeleted ? "Restore" : "Delete"}
      />
    </div>
  );
};

export default CompanyDriversTab;
